<template>
  <div v-if="isPrintPage" class="print-page-isolated">
    <router-view />
  </div>
  <div
    v-else
    class="page-global"
    :class="{ 'dark-mode': store.darkMode, 'compact-tiles': store.compactTiles, 'is-dragging': isDragging, 'is-login-page': isLoginPage }"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
  >
    <!-- Overlay backdrop for mobile -->
    <div
      class="sidebar-backdrop"
      :style="backdropStyle"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar Drawer -->
    <div
      class="sidebar-container"
      :class="{ 'is-dragging': isDragging }"
      :style="sidebarStyle"
    >
      <SideMenuMain v-if="hasSidebarRendered" />
    </div>


    <!-- Main Content Area -->
    <div class="main-layout" :class="{ 'has-top-banner': showTopHeader && hasTopBanner }">
      <Transition name="header-fade">
        <header
          v-if="showTopHeader"
          class="app-header"
          :class="{ 'header-dark-content': isHeaderDarkContent, 'is-recipe-header': isRecipePage }"
          :style="headerStyle"
        >
          <button 
            v-if="showBackButton" 
            class="header-btn back-btn" 
            @click="handleHeaderBack" 
            aria-label="Zurück"
            title="Zurück"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
          </button>
          <button 
            v-else 
            class="header-btn menu-btn" 
            @click="handleToggleSidebar" 
            @mouseenter="hasSidebarRendered = true"
            @touchstart.passive="hasSidebarRendered = true"
            aria-label="Menü öffnen"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
          <!-- Page Title or dynamic Tabs bar -->
          <div v-if="showMainTabs" class="header-tab-bar">
            <router-link 
              to="/recipes" 
              replace
              class="header-tab-item" 
              :class="{ active: activeMainTab === 'recipes' }"
              ref="tabRecipesRef"
            >
              Kochbuch
            </router-link>
            <router-link 
              to="/shopping-list" 
              replace
              class="header-tab-item" 
              :class="{ active: activeMainTab === 'shopping-list' }"
              ref="tabShoppingRef"
            >
              Einkaufsliste
            </router-link>
            <!-- Dynamic moving underline indicator -->
            <div class="header-tab-indicator" ref="tabIndicatorRef" :style="indicatorStyle"></div>
          </div>
          <h1 v-else class="header-title" :title="pageTitle">{{ pageTitle }}</h1>

          <div class="header-actions">
            <button
              v-if="isShoppingListPage && store.shoppingList.length > 0"
              class="header-btn"
              @click="store.shareShoppingList()"
              aria-label="Einkaufsliste teilen"
              title="Einkaufsliste teilen"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
              </svg>
            </button>
            <button
              v-if="isShoppingListPage"
              class="header-btn"
              @click="isShareModalOpen = true"
              aria-label="Liste verwalten"
              title="Einkaufsliste bearbeiten"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>            <!-- Recipe Book Share / Manage Modal button (on /recipes main tab) -->
            <button
              v-if="route.path === '/recipes'"
              class="header-btn"
              @click="isBookSharingModalOpen = true"
              aria-label="Rezeptbuch verwalten & umbenennen"
              title="Rezeptbuch verwalten & umbenennen"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>

            <button
              v-if="isRecipePage && currentRecipeId"
              class="header-btn"
              @click="store.requestShareRecipe = true"
              aria-label="Rezept teilen"
              title="Rezept teilen"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
              </svg>
            </button>

            <button
              v-if="isRecipePage"
              class="header-btn"
              @click="store.requestEditRecipe = true"
              aria-label="Rezept bearbeiten"
              title="Rezept bearbeiten"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>
          </div>

        </header>
      </Transition>

      <!-- App Banners (Offline, Update, Demo) directly under top header -->
      <AppBannerStack 
        :with-header="showTopHeader" 
        @update:active="hasTopBannerFromStack = $event"
      />

      <!-- Global Pending Invitations Banner Stack (when on other pages) -->
      <div 
        v-if="!isShoppingListPage && !isRecipeMainPage && (store.pendingRecipeBookInvitations.length > 0 || store.pendingInvitations.length > 0)" 
        class="global-invitations-stack"
      >
        <div v-for="invite in store.pendingRecipeBookInvitations" :key="'global-book-' + invite.id" class="global-invitation-banner">
          <div class="global-invite-text" @click="router.push('/recipes')">
            🍳 <strong>{{ invite.invitedByName || invite.invitedByEmail }}</strong> lädt dich zum Kochbuch <strong>"{{ invite.bookName }}"</strong> ein!
          </div>
          <div class="global-invite-actions">
            <button class="btn-invite-accept" @click="store.acceptRecipeBookInvitation(invite)">Annehmen</button>
            <button class="btn-invite-decline" @click="store.declineRecipeBookInvitation(invite)">Ablehnen</button>
          </div>
        </div>

        <div v-for="invite in store.pendingInvitations" :key="'global-list-' + invite.id" class="global-invitation-banner">
          <div class="global-invite-text" @click="router.push('/shopping-list')">
            🛒 <strong>{{ invite.invitedByName || invite.invitedByEmail }}</strong> lädt dich zur Einkaufsliste <strong>"{{ invite.listName }}"</strong> ein!
          </div>
          <div class="global-invite-actions">
            <button class="btn-invite-accept" @click="store.acceptListInvitation(invite)">Beitreten</button>
            <button class="btn-invite-decline" @click="store.declineListInvitation(invite)">Ablehnen</button>
          </div>
        </div>
      </div>

      <!-- Dynamic Page Container -->
      <main class="page-content">
        <router-view v-slot="{ Component, route: currentRoute }">
          <transition 
            :name="routerTransitionName" 
            mode="out-in"
          >
            <component 
              :is="Component" 
              :key="currentRoute.path === '/recipes' || currentRoute.path === '/shopping-list' ? 'page-main' : currentRoute.name === 'recipe' ? 'page-recipe' : currentRoute.path" 
            />
          </transition>
        </router-view>
      </main>
    </div>



    <!-- Modal: Manage List Sharing -->
    <ModalManageListSharing v-if="isShareModalOpen" v-model="isShareModalOpen" />

    <!-- Modal: Manage Recipe Book Sharing -->
    <ModalManageRecipeBookSharing v-if="isBookSharingModalOpen" v-model="isBookSharingModalOpen" />

    <!-- Global Confirmation Dialog -->
    <ModalConfirm v-if="store.confirmDialogState.isOpen" />

    <!-- Global Delete With Replacement Dialog -->
    <ModalDeleteWithReplacement v-if="store.deleteWithReplacementState.isOpen" />

    <!-- First-Login Onboarding Setup Dialog -->
    <ModalOnboarding v-if="store.isOnboardingOpen" />

    <!-- Global Notifications Modal -->
    <ModalNotifications
      v-if="store.isNotificationsModalOpen"
      @close="store.isNotificationsModalOpen = false"
    />

    <!-- Global Omnisearch Modal -->
    <ModalGlobalSearch v-if="store.isGlobalSearchOpen" v-model="store.isGlobalSearchOpen" />

    <!-- Global Toast Notifications Stack -->
    <Teleport to="body">
      <div class="toast-stack">
        <transition-group name="toast">
          <div 
            v-for="toast in store.toasts" 
            :key="toast.id" 
            class="global-toast" 
            :class="[toast.type, { 'has-action': toast.action }]"
            @click="store.dismissToast(toast.id)"
            role="button"
            title="Schließen"
          >
            <span class="toast-text">{{ toast.text }}</span>
            <button 
              v-if="toast.action" 
              class="toast-action-btn" 
              @click.stop="triggerToastAction(toast)"
            >
              {{ toast.action.label }}
            </button>
            <span class="toast-close">&times;</span>
          </div>
        </transition-group>
      </div>
    </Teleport>

    <!-- Floating PWA Install Banner -->
    <Teleport to="body">
      <Transition name="slide-up">
        <div v-if="pwa.showBanner.value" class="pwa-install-banner">
          <img :src="'/icon-192x192.png'" alt="App Icon" class="pwa-banner-icon" />
          <div class="pwa-banner-info">
            <span class="pwa-banner-title">Kochbuch & Einkaufsliste</span>
            <span class="pwa-banner-desc">Als App auf dem Startbildschirm installieren</span>
          </div>
          <div class="pwa-banner-actions">
            <button class="btn-banner-install" @click="pwa.promptInstall()">Installieren</button>
            <button class="btn-banner-close" @click="handleDismissPwaBanner()" aria-label="Schließen">✕</button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, watchEffect, onMounted, onUnmounted, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
const SideMenuMain = defineAsyncComponent(() => import('../components/SideMenuMain.vue'))
const ModalManageListSharing = defineAsyncComponent(() => import('../components/modals/ModalManageListSharing.vue'))
const ModalManageRecipeBookSharing = defineAsyncComponent(() => import('../components/modals/ModalManageRecipeBookSharing.vue'))
const ModalConfirm = defineAsyncComponent(() => import('../components/modals/ModalConfirm.vue'))
const ModalDeleteWithReplacement = defineAsyncComponent(() => import('../components/modals/ModalDeleteWithReplacement.vue'))
const ModalOnboarding = defineAsyncComponent(() => import('../components/modals/ModalOnboarding.vue'))
const ModalNotifications = defineAsyncComponent(() => import('../components/modals/ModalNotifications.vue'))
const ModalGlobalSearch = defineAsyncComponent(() => import('../components/modals/ModalGlobalSearch.vue'))
import AppBannerStack from '../components/common/AppBannerStack.vue'
import { useAppStore } from '../stores/appStore'
import { usePwaInstall } from '../composables/usePwaInstall'
import { usePwaUpdate } from '../composables/usePwaUpdate'
import { navigateAppBack } from '../composables/useAppNavigation'
import { useNetworkState } from '../composables/useNetworkState'
import { rgbToHex, adaptColor, isLightColor } from '../utils/adaptColor'

const route = useRoute()
const router = useRouter()
const store = useAppStore()
const isShareModalOpen = ref(false)
const isBookSharingModalOpen = ref(false)

const { effectiveOffline } = useNetworkState(() => {
  store.showToast('Wieder online – Daten synchronisiert ✓', 'success')
})

const isLoginPage = computed(() => route.path === '/login')
const isPrintPage = computed(() => route.meta?.isPrint === true || route.name === 'recipe-print' || route.path.endsWith('/print'))

const pwa = usePwaInstall()

const handleDismissPwaBanner = () => {
  pwa.dismissBanner()
  store.showToast(
    'Installationshinweis ausgeblendet. Du kannst die App jederzeit in den Einstellungen installieren.',
    'success',
    {
      label: 'Einstellungen',
      callback: () => {
        router.push('/settings?tab=general&highlight=pwaInstall')
      }
    }
  )
}

const triggerToastAction = (toast: any) => {
  if (toast.action && toast.action.callback) {
    toast.action.callback()
  }
  store.dismissToast(toast.id)
}


const { needRefresh } = usePwaUpdate()

const hasTopBannerFromStack = ref(false)
const hasTopBanner = computed(() => {
  return Boolean(hasTopBannerFromStack.value) || 
         Boolean(needRefresh?.value) || 
         Boolean(effectiveOffline.value) || 
         Boolean(store.isDemoMode)
})

import { useSwipeDrawer } from '../composables/useSwipeDrawer'

const {
  isDragging,
  pageDragOffset,
  gestureType,
  mainTabProgress,
  activeMainTab,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  sidebarStyle,
  backdropStyle,
  isSidebarOpen,
  toggleSidebar,
  closeSidebar
} = useSwipeDrawer()

const hasSidebarRendered = ref(typeof window !== 'undefined' && window.innerWidth >= 992)

const handleToggleSidebar = () => {
  hasSidebarRendered.value = true
  toggleSidebar()
}

watch([isSidebarOpen, isDragging], ([open, dragging]) => {
  if (open || dragging) {
    hasSidebarRendered.value = true
  }
})

// Automatically close sidebar on route change (for mobile)
watch(route, () => {
  closeSidebar()
})

// ── Page meta ─────────────────────────────────────────────────────────────────
const recipeIdList = computed(() => store.resolvedRecipeIdsList)

const displayRecipeId = computed(() => {
  const currentId = route.params.id as string
  if (!currentId || route.name !== 'recipe') return null
  
  if (store.activeRecipeIdOverride) {
    return store.activeRecipeIdOverride
  }

  if (isDragging.value && gestureType.value === 'recipe-switch') {
    const list = recipeIdList.value
    const idx = list.indexOf(currentId)
    if (idx !== -1 && list.length > 1) {
      if (pageDragOffset.value <= -80) {
        return list[(idx + 1) % list.length]
      } else if (pageDragOffset.value >= 80) {
        return list[(idx - 1 + list.length) % list.length]
      }
    }
  }
  return currentId
})

const pageTitle = computed(() => {
  if (route.path === '/shopping-list') {
    return 'Einkaufsliste'
  }
  if (route.name === 'recipe') {
    const activeId = displayRecipeId.value
    if (activeId) {
      const r = store.recipes.find(rec => rec.id === activeId)
      if (r) return r.name
    }
  }
  return (route.meta?.title as string) || 'Kochbuch'
})

const isShoppingListPage = computed(() => {
  return route.path === '/shopping-list'
})

const isRecipeMainPage = computed(() => {
  return route.path === '/recipes'
})

const isRecipePage = computed(() => {
  return route.name === 'recipe'
})

const currentRecipeId = computed(() => {
  return (route.params.id as string) || ''
})

const showMainTabs = computed(() => {
  return route.path === '/recipes' || route.path === '/shopping-list'
})

const showTopHeader = computed(() => {
  if (route.name === 'not-found') return false
  const hiddenRoutes = [
    '/articles',
    '/article-categories',
    '/recipe-categories',
    '/units',
    '/settings',
    '/trash',
    '/user',
    '/support',
    '/privacy',
    '/index',
    '/login'
  ]
  return !hiddenRoutes.includes(route.path)
})

const showBackButton = computed(() => {
  // Only sub-detail pages (like single recipe view) show the back button.
  // All top-level drawer pages (Stammdaten, Settings, Trash, etc.) show the drawer menu button.
  return route.name === 'recipe'
})

const lastRecipeColor = ref<string | null>(null)

const activeHeaderRgb = computed(() => {
  let color = '139, 195, 74'
  if (route.name === 'recipe') {
    const activeId = displayRecipeId.value || currentRecipeId.value
    if (activeId) {
      if (store.recipeColors[activeId]) {
        color = store.recipeColors[activeId]
      } else {
        const rec = store.recipes.find(r => r.id === activeId)
        if (rec?.dominantColor) {
          color = adaptColor(rec.dominantColor, store.darkMode)
        } else if (store.activeHeaderColor) {
          color = store.activeHeaderColor
        } else if (lastRecipeColor.value) {
          color = lastRecipeColor.value
        }
      }
    } else if (lastRecipeColor.value) {
      color = lastRecipeColor.value
    }
    lastRecipeColor.value = color
  } else {
    lastRecipeColor.value = null
    color = store.activeHeaderColor || '139, 195, 74'
  }
  return color
})

const isHeaderDarkContent = computed(() => {
  return isLightColor(activeHeaderRgb.value)
})

const headerStyle = computed(() => {
  return {
    backgroundColor: `rgb(${activeHeaderRgb.value})`,
    '--header-bg-rgb': activeHeaderRgb.value
  }
})

const headerHexColor = computed(() => {
  // On pages without top header (Settings, Login, Admin, etc.), match the page background
  if (!showTopHeader.value) {
    return store.darkMode ? '#1e1e1e' : '#ffffff'
  }
  // On pages with top header (recipes, shopping-list, recipe detail):
  // Dynamically adapt to activeHeaderRgb so Android status bar / title bar matches seamlessly without flashing green
  return rgbToHex(activeHeaderRgb.value)
})

// Update theme-color meta tags so Android status bar / title bar, navigation bar / footer line, and desktop PWA title bar match the active page.
watchEffect(() => {
  const hex = headerHexColor.value

  let metaDefault = document.getElementById('theme-color-meta') as HTMLMetaElement | null
  let metaLight = document.getElementById('theme-color-meta-light') as HTMLMetaElement | null
  let metaDark = document.getElementById('theme-color-meta-dark') as HTMLMetaElement | null

  if (!metaDefault) {
    metaDefault = document.querySelector('meta[name="theme-color"]:not([media])') as HTMLMetaElement | null
  }
  if (!metaDefault) {
    metaDefault = document.createElement('meta')
    metaDefault.name = 'theme-color'
    metaDefault.id = 'theme-color-meta'
    document.head.appendChild(metaDefault)
  }

  if (!metaLight) {
    metaLight = document.querySelector('meta[name="theme-color"][media*="light"]') as HTMLMetaElement | null
  }
  if (!metaLight) {
    metaLight = document.createElement('meta')
    metaLight.name = 'theme-color'
    metaLight.id = 'theme-color-meta-light'
    metaLight.setAttribute('media', '(prefers-color-scheme: light)')
    document.head.appendChild(metaLight)
  }

  if (!metaDark) {
    metaDark = document.querySelector('meta[name="theme-color"][media*="dark"]') as HTMLMetaElement | null
  }
  if (!metaDark) {
    metaDark = document.createElement('meta')
    metaDark.name = 'theme-color'
    metaDark.id = 'theme-color-meta-dark'
    metaDark.setAttribute('media', '(prefers-color-scheme: dark)')
    document.head.appendChild(metaDark)
  }

  // Update content and preserve media queries on all theme-color elements.
  // Android Chromium matches theme-color via prefers-color-scheme for status and bottom navigation bars.
  metaDefault.setAttribute('content', hex)
  metaDefault.removeAttribute('media')

  metaLight.setAttribute('content', hex)
  metaLight.setAttribute('media', '(prefers-color-scheme: light)')

  metaDark.setAttribute('content', hex)
  metaDark.setAttribute('media', '(prefers-color-scheme: dark)')

  const allThemeMetas = document.querySelectorAll('meta[name="theme-color"]')
  allThemeMetas.forEach(el => {
    el.setAttribute('content', hex)
  })

  // Force Chromium / Android WebAPK / PWA to re-evaluate and trigger both status bar & navigation bar coloring.
  // Re-appending all meta elements ensures that both media-query observers (light & dark mode)
  // as well as the fallback observer trigger DidChangeThemeColor in the browser.
  const tagsToRefresh = [metaLight, metaDark, metaDefault]
  tagsToRefresh.forEach(tag => {
    if (tag && tag.parentNode) {
      const parent = tag.parentNode
      parent.removeChild(tag)
      parent.appendChild(tag)
    }
  })
}, { flush: 'sync' })

const lastNonSettingsRoute = ref<string>(
  localStorage.getItem('last_non_settings_route') || '/recipes'
)

const handleHeaderBack = () => {
  navigateAppBack(router)
}

const tabShoppingRef = ref<any>(null)
const tabRecipesRef = ref<any>(null)
const tabBounds = ref({ lRecipes: 0, wRecipes: 70, lShopping: 85, wShopping: 100 })

let rAFId: number | null = null
const updateTabBounds = () => {
  if (typeof window === 'undefined') return
  if (rAFId) cancelAnimationFrame(rAFId)
  rAFId = requestAnimationFrame(() => {
    rAFId = null
    const elRecipes = tabRecipesRef.value?.$el || tabRecipesRef.value
    const elShopping = tabShoppingRef.value?.$el || tabShoppingRef.value
    if (elRecipes && elShopping) {
      const lR = elRecipes.offsetLeft || 0
      const wR = elRecipes.offsetWidth || 70
      const lS = elShopping.offsetLeft || 85
      const wS = elShopping.offsetWidth || 100
      const cur = tabBounds.value
      if (cur.lRecipes !== lR || cur.wRecipes !== wR || cur.lShopping !== lS || cur.wShopping !== wS) {
        tabBounds.value = { lRecipes: lR, wRecipes: wR, lShopping: lS, wShopping: wS }
      }
    }
  })
}

watch([tabRecipesRef, tabShoppingRef], () => {
  updateTabBounds()
})

// Also re-measure after route changes (in case the header re-mounts)
watch(() => route.path, () => {
  updateTabBounds()
})

const handleResize = () => {
  updateTabBounds()
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize, { passive: true })
    updateTabBounds()
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
    if (rAFId) cancelAnimationFrame(rAFId)
  }
})

const indicatorStyle = computed(() => {

  let progress = 0

  if (isDragging.value && gestureType.value === 'page-switch' && mainTabProgress.value !== null) {
    progress = mainTabProgress.value
  } else {
    progress = activeMainTab.value === 'shopping-list' ? 1 : 0
  }
  
  const { lRecipes, wRecipes, lShopping, wShopping } = tabBounds.value
  const left = lRecipes + progress * (lShopping - lRecipes)
  const width = wRecipes + progress * (wShopping - wRecipes)
  
  // No transition during drag or when explicitly suppressed
  const noTransition = isDragging.value && gestureType.value === 'page-switch'
  
  return {
    left: `${left}px`,
    width: `${width}px`,
    transition: noTransition
      ? 'none' 
      : 'left 0.32s cubic-bezier(0.3, 0.8, 0.4, 1), width 0.32s cubic-bezier(0.3, 0.8, 0.4, 1)'
  }
})

const getInitialRouterTransition = () => {
  const path = window.location.pathname
  if (path === '/recipes' || path === '/shopping-list' || path === '/' || path.startsWith('/recipe/')) {
    return 'none'
  }
  return 'fade'
}

const routerTransitionName = ref(getInitialRouterTransition())

router.beforeEach((to, from, next) => {
  if (from && from.path && from.path !== '/settings' && from.path !== '/login') {
    const routeToSave = from.fullPath || from.path
    lastNonSettingsRoute.value = routeToSave
    localStorage.setItem('last_non_settings_route', routeToSave)
  }

  if (to.name === 'recipe' && from?.name === 'recipe') {
    routerTransitionName.value = 'none'
  } else if (to.name === 'settings' && from?.name === 'settings') {
    routerTransitionName.value = 'none'
  } else if (
    (to.path === '/recipes' || to.path === '/shopping-list' || to.name === 'recipe') &&
    (!from || from.path === '/' || from.path === '' || from.path === '/recipes' || from.path === '/shopping-list' || from.name === 'recipe')
  ) {
    routerTransitionName.value = 'none'
  } else {
    routerTransitionName.value = 'fade'
  }
  next()
})

// pageContentStyle removed - sliding transition handled directly by PageRecipe / PageMain sliders
</script>

<style scoped>
.print-page-isolated {
  width: 100%;
  min-height: 100%;
  background: #ffffff;
  color: #111111;
  position: relative;
  z-index: 1;
}

@media print {
  .print-page-isolated {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow: visible !important;
    position: static !important;
    margin: 0 !important;
    padding: 0 !important;
  }
}

.page-global {
  display: flex;
  height: 100%;
  height: 100dvh;
  max-height: 100%;
  width: 100%;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* Correct background in both light and dark mode — prevents white flash during drawer animation */
  background-color: var(--bg-primary);
  /* Let browser handle vertical scrolling; we own horizontal swipes */
  touch-action: pan-y;
  user-select: none;
}

/* Sidebar Container */
.sidebar-container {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 100;
  /* transition active only when NOT dragging */
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
  user-select: text; /* re-enable text selection inside sidebar */
  touch-action: pan-y;
}

/* Disable transition while user is actively dragging */
.sidebar-container.is-dragging {
  transition: none;
}

/* Sidebar backdrop for mobile viewports */
.sidebar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.55);
  z-index: 99;
  /* opacity & pointer-events driven by inline style (backdropStyle computed) */
}

/* Main Layout content */
.main-layout {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  overflow: hidden;
  background-color: var(--bg-secondary);
  position: relative;
  --page-content-padding-top: 56px;
}

.main-layout.has-top-banner {
  --page-content-padding-top: 0px !important;
}

/* Header bar styling */
.app-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  height: 56px;
  background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(0, 0, 0, 0.1));
  color: #ffffff;
  padding: 0 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 20;
  flex-shrink: 0;
  transition: background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s ease;
}

/* Flat clean typography without shadow on medium/dark headers */
.app-header:not(.header-dark-content) .header-title {
  text-shadow: none;
}

.app-header:not(.header-dark-content) .header-btn {
  filter: none;
}

/* Light header background: soft dark charcoal/slate text & icons */
.app-header.header-dark-content {
  color: #2c3437;
  background-image: linear-gradient(135deg, rgba(0, 0, 0, 0.01), rgba(0, 0, 0, 0.05));
}

.app-header.header-dark-content .header-title {
  color: #2c3437;
  font-weight: 700;
  text-shadow: none;
}

.app-header.header-dark-content .header-btn {
  color: #2c3437;
  filter: none;
}

/* Recipe Page Header: Ton-in-Ton Blockschrift mit optimierter Buchstabendichte fuer lange Titel */
.app-header.is-recipe-header .header-title {
  font-size: clamp(1.02rem, 3.8vw, 1.25rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  margin-left: 0.5rem;
  margin-right: 0.25rem;
}

.app-header.is-recipe-header:not(.header-dark-content) .header-title {
  color: color-mix(in srgb, rgb(var(--header-bg-rgb)) 20%, #ffffff);
}

.app-header.is-recipe-header.header-dark-content .header-title {
  color: color-mix(in srgb, rgb(var(--header-bg-rgb)) 50%, #101516);
}

@media (hover: hover) and (pointer: fine) {
  .app-header.header-dark-content .header-btn:hover {
    background-color: rgba(44, 52, 55, 0.08);
  }
}

.app-header.header-dark-content .header-tab-item {
  color: rgba(44, 52, 55, 0.65);
}

@media (hover: hover) and (pointer: fine) {
  .app-header.header-dark-content .header-tab-item:hover {
    color: #2c3437;
  }
}

.app-header.header-dark-content .header-tab-item.active {
  color: #2c3437;
}

.app-header.header-dark-content .header-tab-indicator {
  background-color: #2c3437;
}

.header-fade-enter-active {
  transition: opacity 0.2s ease 0.2s !important;
}
.header-fade-leave-active {
  transition: opacity 0.2s ease !important;
  pointer-events: none;
}
.header-fade-enter-from,
.header-fade-leave-to {
  opacity: 0 !important;
}


.header-btn {
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: color 0.2s ease;
  position: relative;
  -webkit-tap-highlight-color: transparent !important;
  user-select: none;
  -webkit-user-select: none;
}

@media (hover: hover) and (pointer: fine) {
  .header-btn:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
}

.header-btn.notification-btn {
  position: relative;
}

.header-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: #f44336;
  color: #ffffff;
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 17px;
  height: 17px;
  line-height: 17px;
  padding: 0 4px;
  border-radius: 9px;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  pointer-events: none;
  box-sizing: border-box;
}

.header-btn svg {
  width: 24px;
  height: 24px;
}

.header-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  margin-left: 1rem;
  flex-grow: 1;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Header Tab Bar */
.header-tab-bar {
  display: flex;
  align-items: center;
  margin-left: 1rem;
  flex-grow: 1;
  height: 56px;
  gap: 1.5rem;
  position: relative;
}

@media (min-width: 992px) {
  .header-tab-bar {
    margin-left: 0.5rem;
  }
}

.header-tab-item {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  height: 56px;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
  box-sizing: border-box;
  cursor: pointer;
}

.header-tab-item:hover {
  color: #ffffff;
}

.header-tab-item.active {
  color: #ffffff;
}

.header-tab-indicator {
  position: absolute;
  bottom: 0;
  height: 3px;
  background-color: #ffffff;
  border-radius: 3px 3px 0 0;
  will-change: left, width;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

/* Page content area */
.page-content {
  flex-grow: 1;
  overflow: hidden;
  height: 100%;
  padding: 0;
  position: relative;
  will-change: transform;
}

/* Hide sidebar completely on login page (all viewport sizes) */
.is-login-page .sidebar-container {
  display: none !important;
}

/* Desktop Styles */
@media (min-width: 992px) {
  .page-global {
    max-width: 1200px;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: 0 0 50px rgba(0, 0, 0, 0.28);
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
  }

  .sidebar-container {
    position: relative;
    transform: translateX(0);
    z-index: 1;
    border-right: 1px solid var(--border-color);
  }

  .sidebar-backdrop {
    display: none;
  }

  .menu-btn {
    display: none; /* Hide hamburger menu on desktop when docked */
  }

  .header-title {
    margin-left: 0.5rem; /* Less margin when button is hidden */
  }

  .drag-handle-strip {
    display: none; /* Not needed on desktop – drawer is always docked */
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Toast Stack and Toast Notifications */
.toast-stack {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-toast, 50000);
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  pointer-events: none;
  width: 100%;
  max-width: 640px;
  padding: 0 16px;
  box-sizing: border-box;
}

.global-toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  color: white;
  background-color: #323232;
  font-weight: 500;
  font-size: 0.9rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  min-width: 280px;
  max-width: 100%;
  width: fit-content;
  box-sizing: border-box;
}

.global-toast.success {
  background-color: #4CAF50;
}

.global-toast.error {
  background-color: #f44336;
}

.global-toast.warning {
  background-color: #ff9800;
}

.global-toast.info {
  background-color: #2196F3;
}

.toast-text {
  flex-grow: 1;
}

.toast-close {
  font-size: 1.2rem;
  line-height: 1;
  font-weight: 700;
  opacity: 0.7;
}

.global-toast:hover .toast-close {
  opacity: 1;
}

.toast-action-btn {
  background: white;
  border: none;
  border-radius: 4px;
  color: #4CAF50;
  padding: 4px 8px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s, transform 0.1s;
  margin-right: 4px;
}

.global-toast.error .toast-action-btn {
  color: #f44336;
}

.toast-action-btn:hover {
  opacity: 0.9;
  transform: scale(1.05);
}

.toast-action-btn:active {
  transform: scale(0.95);
}

/* Toast Transitions (Fade Out & Slide) */
.toast-enter-active {
  transition: opacity 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.toast-leave-active {
  transition: opacity 0.35s ease-out, transform 0.35s ease-out;
  pointer-events: none !important;
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.92);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}



/* PWA Install Banner */
.pwa-install-banner {
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 2rem);
  max-width: 480px;
  background: var(--bg-card);
  border: 1px solid var(--border-medium);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  border-radius: 16px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 9999;
  box-sizing: border-box;
}

.pwa-banner-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  flex-shrink: 0;
  object-fit: cover;
}

.pwa-banner-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
}

.pwa-banner-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-primary);
}

.pwa-banner-desc {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.pwa-banner-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-banner-install {
  background: #8bc34a;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.4rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s, background-color 0.15s;
}

.btn-banner-install:hover {
  background: #7cb342;
  transform: scale(1.03);
}

.btn-banner-close {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.2rem;
  line-height: 1;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}

.has-top-banner :deep(.page-main-container),
.has-top-banner :deep(.page-recipe-wrapper-carousel) {
  padding-top: 0 !important;
}

/* Global Invitations Banner Stack */
.global-invitations-stack {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: rgba(139, 195, 75, 0.12);
  border-bottom: 1px solid rgba(139, 195, 75, 0.25);
  flex-shrink: 0;
  position: relative;
  z-index: 50;
}

.global-invitation-banner {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: var(--bg-card);
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(139, 195, 75, 0.3);
}

@media (min-width: 480px) {
  .global-invitation-banner {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
}

.global-invite-text {
  font-size: 0.85rem;
  color: var(--text-primary);
  line-height: 1.4;
  cursor: pointer;
  text-align: left;
}

.global-invite-text:hover {
  text-decoration: underline;
}

.global-invite-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-shrink: 0;
}

.btn-invite-accept,
.btn-invite-decline {
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.btn-invite-accept {
  background-color: var(--color-brand);
  color: #fff;
  border: none;
}

.btn-invite-accept:hover {
  background-color: var(--color-brand-hover);
}

.btn-invite-decline {
  background-color: transparent;
  color: var(--text-muted);
  border: 1px solid var(--border-color);
}

.btn-invite-decline:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: var(--text-primary);
}

</style>
